import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, GripVertical, Lock, Settings2, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { FieldAccessEditor, isRestricted } from '@/features/records/components/FieldAccessEditor'
import { isDataField } from '@/features/records/utils/fieldAccess'
import { isLayoutField } from '@/features/records/utils/layoutField'
import { isHiddenOnPublicForm } from '@/features/records/utils/prefill'
import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'
import { cn } from '@/utils/cn'

import { FieldBehaviorSettings } from './FieldBehaviorSettings'
import { FieldConstraints, SelectOptionsEditor } from './FieldConstraints'
import { FIELD_CONFIG } from './fieldTypeConfig'
export function CanvasFieldCard({
  field, allFields, onUpdate, onDelete, keyError, canManageAccess = false, isTitleField = false,
  isMobileViewport = false, onMoveUp, onMoveDown,
}: {
  field: FormField
  allFields: FormField[]
  onUpdate: (id: string, updates: Partial<FormField>) => void
  onDelete: (id: string) => void
  keyError?: string | null
  /** Owners and admins only: the access settings are not sent to anyone else. */
  canManageAccess?: boolean
  isTitleField?: boolean
  isMobileViewport?: boolean
  onMoveUp?: () => void
  onMoveDown?: () => void
}) {
  const [showConstraints, setShowConstraints] = useState(isLayoutField(field))
  const [showBehavior, setShowBehavior] = useState(false)
  const [showAccess, setShowAccess] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    data: { source: 'canvas', field },
    disabled: isMobileViewport,
  })
  const cfg = FIELD_CONFIG[field.type] ?? FIELD_CONFIG[FormFieldType.TEXT]
  const Icon = cfg.icon
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const validations = field.validations as Record<string, unknown> | null
  const constraintCount = validations
    ? Object.values(validations).filter(
        (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
      ).length
    : 0

  // Do NOT return a different element when isDragging. Returning a different
  // JSX branch causes React to re-mount the DOM node mid-drag, which forces
  // dnd-kit to re-measure rects and can produce a height mismatch (the fixed
  // h-[88px] placeholder doesn't match the real card height). Keep the same
  // node in the tree and only change its visual appearance.
  return (
    <Card
      ref={setNodeRef}
      style={style}
      data-field-id={field.id}
      className={`group flex items-start gap-2 rounded-lg border border-border p-3 text-foreground shadow-none sm:gap-3 sm:p-4 ${isDragging ? 'bg-primary/5 opacity-50 pointer-events-none' : 'bg-card'}`}
    >

      {/* Drag handle */}
      <button
        className="mt-1 hidden cursor-grab text-muted-foreground/50 transition-colors hover:text-muted-foreground active:cursor-grabbing md:ml-2 md:block"
        {...attributes} {...listeners}
      >
        <GripVertical className="h-5 w-5" />
      </button>

      {/* Type icon badge */}
      <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${cfg.bg}`}>
        <Icon className={`h-4 w-4 ${cfg.iconColor}`} />
      </div>

      {/* Main content */}
      <div className={cn('min-w-0 flex-1', !isCollapsed && 'space-y-2.5')}>
        {/* Type badge + required pill */}
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${cfg.badge}`}>
            {cfg.label}
          </span>
          {isCollapsed && <span className="min-w-0 truncate text-sm font-medium text-foreground">{field.label || 'Untitled field'}</span>}
          {field.required && (
            <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
              Required
            </span>
          )}
          {isRestricted(field.access) && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              <Lock className="h-3 w-3" aria-hidden="true" /> Restricted
            </span>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-0.5">
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 md:hidden" aria-label={`Move ${field.label || cfg.label} up`} title="Move up" disabled={!onMoveUp} onClick={onMoveUp}>
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button type="button" variant="ghost" size="icon" className="h-8 w-8 md:hidden" aria-label={`Move ${field.label || cfg.label} down`} title="Move down" disabled={!onMoveDown} onClick={onMoveDown}>
              <ArrowDown className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${field.label || cfg.label}`}
              aria-expanded={!isCollapsed}
              title={isCollapsed ? 'Expand field' : 'Collapse field'}
              onClick={() => setIsCollapsed((previous) => !previous)}
            >
              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {!isCollapsed && <>
        {/* Key + Label editors */}
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Key</Label>
            <Input value={field.key} onChange={(e) => onUpdate(field.id, { key: e.target.value })}
              aria-invalid={!!keyError}
              className="h-8 font-mono text-sm" placeholder="field_key" />
            {keyError && <p className="text-[11px] text-destructive">{keyError}</p>}
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Label</Label>
            <Input value={field.label} onChange={(e) => onUpdate(field.id, { label: e.target.value })}
              className="h-8 text-sm" placeholder="Field Label" />
          </div>
        </div>

        {/* Display, visibility, and uniqueness settings */}
        <div>
          <button
            type="button"
            onClick={() => setShowBehavior((previous) => !previous)}
            aria-expanded={showBehavior}
            className="flex w-full items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <Settings2 className="h-3.5 w-3.5" />
            <span>Display & behavior</span>
            {showBehavior ? <ChevronUp className="ml-auto h-3 w-3" /> : <ChevronDown className="ml-auto h-3 w-3" />}
          </button>
          {showBehavior && <div className="mt-2 rounded-lg border border-border bg-muted/50 p-3"><FieldBehaviorSettings field={field} allFields={allFields} onUpdate={onUpdate} /></div>}
        </div>

        {/* Choice options editor */}
        {([FormFieldType.SELECT, FormFieldType.RADIO, FormFieldType.CHECKBOX] as FormFieldType[]).includes(field.type) && (
          <SelectOptionsEditor field={field} onUpdate={onUpdate} />
        )}

        {/* Constraints toggle */}
        <div>
          <button
            onClick={() => setShowConstraints((p) => !p)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
          >
            <Settings2 className="h-3.5 w-3.5" />
            <span>Constraints</span>
            {constraintCount > 0 && (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                {constraintCount} active
              </span>
            )}
            {showConstraints
              ? <ChevronUp className="h-3 w-3 ml-auto" />
              : <ChevronDown className="h-3 w-3 ml-auto" />}
          </button>
          {showConstraints && (
            <div className="mt-2 rounded-lg border border-border bg-muted/50 p-3">
              <FieldConstraints field={field} onUpdate={onUpdate} />
            </div>
          )}
        </div>

        {/* Access: who may see and change this field (owners and admins only) */}
        {canManageAccess && (
          <div>
            <button
              onClick={() => setShowAccess((p) => !p)}
              aria-expanded={showAccess}
              className="flex w-full items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
            >
              <Lock className="h-3.5 w-3.5" />
              <span>Access</span>
              {showAccess
                ? <ChevronUp className="h-3 w-3 ml-auto" />
                : <ChevronDown className="h-3 w-3 ml-auto" />}
            </button>
            {showAccess && (
              <div className="mt-2 rounded-lg border border-border bg-muted/50 p-3">
                <FieldAccessEditor
                  value={field.access}
                  onChange={(access) => onUpdate(field.id, { access })}
                  mustStayVisible={isTitleField ? 'The title field names the record everywhere, so everyone sees it. You can still limit who may edit it.' : undefined}
                />
              </div>
            )}
          </div>
        )}
        </>}
      </div>

      {/* Required toggle */}
      {isDataField(field) && <div className="flex shrink-0 flex-col items-center gap-1 pt-1">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Req</span>
        <Switch checked={field.required} disabled={isHiddenOnPublicForm(field)} onCheckedChange={(v) => onUpdate(field.id, { required: v })} />
      </div>}

      {/* Delete */}
      <Button type="button" variant="ghost" size="icon" aria-label={`Delete ${field.label || cfg.label}`} title="Delete field"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10
                   opacity-100 transition-opacity md:text-muted-foreground/50 md:opacity-0 md:group-hover:opacity-100"
        onClick={() => onDelete(field.id)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </Card>
  )
}
